const cities = [
  { name: "Houma", zips: "70360, 70363, 70364" },
  { name: "Thibodaux", zips: "70301" },
  { name: "Gray", zips: "70359" },
  { name: "Schriever", zips: "70395" },
  { name: "Morgan City", zips: "70380" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, crew quarters, or office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send documents directly to us — we handle every detail from there. No New Orleans trip required.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed before submission to reduce errors — critical for offshore workers on tight rotation schedules.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function HoumaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Houma &amp; the Bayou Region
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Houma, Louisiana is the heartbeat of the Bayou Region — a community built on the intersection of bayou culture, offshore energy, and maritime industry. Terrebonne Parish is home to one of the densest concentrations of offshore oil and gas workers in North America. Helicopter service companies, dive contractors, ROV operators, and marine supply firms cluster along the bayous and inland canals that connect Houma to the Gulf of Mexico. The rotational lifestyle these workers live — two weeks on, two weeks off — creates a unique passport urgency pattern that no other Louisiana city replicates.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              When an offshore worker receives a contract modification that requires an international mobilization — Aberdeen, Singapore, Brazil, West Africa — the timeline is often measured in days, not weeks. A passport sitting at home expired during the COVID travel shutdown becomes an immediate crisis. We handle these situations regularly, providing same-day-initiated processing that routes applications through reserved agency access so the worker makes their mobilization window.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the offshore industry, Houma and Terrebonne Parish have strong Cajun heritage tourism and cruise travel patterns. Families in Gray, Schriever, and Thibodaux book New Orleans cruises seasonally, often discovering expired passports during the booking process. The area's network of bayou fishing and hunting camps also attracts retirees and seasonal visitors who may not have traveled internationally in years — and discover their passport expired when they book a Caribbean trip or heritage tour to France and Quebec.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Nicholls State University in Thibodaux sends students abroad for culinary arts programs in France, marine biology research in the Caribbean, and business exchanges in Latin America. These students often discover the passport timeline crunch in October or February — after acceptance but before departure orientation. Fast Passport Center provides the expedited service that Houma's diverse traveler base needs: dedicated agents, document review before submission, and processing as fast as 24 hours.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Houma, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Terrebonne &amp; Lafourche Parishes</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — start online, ship overnight, we handle the rest
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">No New Orleans drive required for most services</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, then <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx in Houma, Thibodaux, or Gray. Most offshore workers and Bayou Region clients ship overnight — no trip to New Orleans required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the Bayou Region — start online, ship overnight, and we handle the rest. No need to drive to New Orleans for regional agency appointments.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Baton Rouge", href: "/cities/baton-rouge" },
                    { name: "Lafayette", href: "/cities/lafayette" },
                    { name: "Lake Charles", href: "/cities/lake-charles" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Bayou Region Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}